import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { trackTutorialEvent } from '../utils/analytics';
import { TOUR_STEPS } from './tourSteps';

/** 0 = tour off, 1..N = current step index (1-based) */
export type TourStepIndex = number;

export interface TourContextValue {
  /** Current step (0 = off, 1 = first step, etc.) */
  tourStep: TourStepIndex;
  /** Total number of steps */
  totalSteps: number;
  /** Step config for the current step, or null if tour is off */
  currentStepConfig: (typeof TOUR_STEPS)[number] | null;
  startTour: () => void;
  nextStep: () => void;
  skipTour: () => void;
}

const TourContext = createContext<TourContextValue | null>(null);

export interface TourProviderProps {
  children: React.ReactNode;
  /** Called when the user completes or skips the tour (for hybrid persistence). */
  onTourComplete?: () => void | Promise<void>;
}

export function TourProvider({ children, onTourComplete }: TourProviderProps) {
  const [tourStep, setTourStep] = useState<TourStepIndex>(0);
  const totalSteps = TOUR_STEPS.length;
  const currentStepConfig =
    tourStep >= 1 && tourStep <= totalSteps ? TOUR_STEPS[tourStep - 1]! : null;

  const startTour = useCallback(() => {
    trackTutorialEvent('tour_start');
    setTourStep(1);
  }, []);

  const nextStep = useCallback(() => {
    setTourStep((prev) => {
      if (prev >= 1 && prev <= totalSteps) {
        const stepConfig = TOUR_STEPS[prev - 1]!;
        trackTutorialEvent('step_next', {
          tutorial_step_id: stepConfig.id,
          tutorial_step_index: prev,
          tutorial_step_title: stepConfig.title,
        });
      }
      if (prev >= totalSteps) {
        trackTutorialEvent('step_finish', {
          tutorial_step_id: TOUR_STEPS[totalSteps - 1]?.id,
          tutorial_step_index: totalSteps,
          tutorial_step_title: TOUR_STEPS[totalSteps - 1]?.title,
        });
        setTimeout(() => onTourComplete?.(), 0);
        return 0;
      }
      return prev + 1;
    });
  }, [totalSteps, onTourComplete]);

  const skipTour = useCallback(() => {
    if (tourStep >= 1 && tourStep <= totalSteps) {
      const stepConfig = TOUR_STEPS[tourStep - 1]!;
      trackTutorialEvent('step_skip', {
        tutorial_step_id: stepConfig.id,
        tutorial_step_index: tourStep,
        tutorial_step_title: stepConfig.title,
      });
    }
    setTourStep(0);
    setTimeout(() => onTourComplete?.(), 0);
  }, [onTourComplete, tourStep, totalSteps]);

  useEffect(() => {
    if (currentStepConfig && tourStep >= 1) {
      trackTutorialEvent('step_view', {
        tutorial_step_id: currentStepConfig.id,
        tutorial_step_index: tourStep,
        tutorial_step_title: currentStepConfig.title,
      });
    }
  }, [tourStep, currentStepConfig]);

  return (
    <TourContext.Provider
      value={{
        tourStep,
        totalSteps,
        currentStepConfig,
        startTour,
        nextStep,
        skipTour,
      }}
    >
      {children}
    </TourContext.Provider>
  );
}

export function useTour() {
  const ctx = useContext(TourContext);
  if (!ctx) {
    throw new Error('useTour must be used within TourProvider');
  }
  return ctx;
}

export { TOUR_STEPS };
